import { useDispatch, useSelector } from 'react-redux';
import {
  errorCodeSelector,
  refreshTokenSelector,
  tokenSelector,
} from './store/selector';
import { useCallback } from 'react';
import { PostLoginRequest } from './interfaces/post-login';
import { authApiService } from './services/api';
import {
  authClearAction,
  postLoginFailureApiAction,
  postLoginInitApiAction,
  postLoginSuccessApiAction,
  postSignUpFailureApiAction,
  postSignUpInitApiAction,
  postSignUpSuccessApiAction,
} from './store/reducer';
import { globalClearAction } from '../global/store/reducer';
import { meClearAction } from '../me/store/reducer';
import { userClearAction } from '../user/store/reducer';
import { PostSignUpRequest } from './interfaces/post-sign-up';

export function useAuthCore() {
  const token = useSelector(tokenSelector);
  const refreshToken = useSelector(refreshTokenSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const login = useCallback(
    async (body: PostLoginRequest, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(postLoginInitApiAction());
        const response = await authApiService.postLogin(body);
        dispatch(postLoginSuccessApiAction(response));
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postLoginFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const signUp = useCallback(
    async (body: PostSignUpRequest, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(postSignUpInitApiAction());
        const response = await authApiService.postSignUp(body);
        dispatch(postSignUpSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postSignUpFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const clearAuth = useCallback(() => {
    dispatch(authClearAction());
  }, [dispatch]);

  const logout = useCallback(() => {
    dispatch(authClearAction());
    dispatch(globalClearAction());
    dispatch(meClearAction());
    dispatch(userClearAction());
  }, [dispatch]);

  return {
    token,
    refreshToken,
    errorCode,
    login,
    signUp,
    logout,
    clearAuth,
  };
}
